import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { ApiErrorCodes } from '../../../../core/enums/ErrorCodes';
import { IShowPriceCategory } from '../../../shows/core/interfaces/IShowPriceCategory';
import { VouchersService } from '../../../vouchers/core/services/VouchersService';
import { ICartItem } from '../interfaces/ICartItem';
import { CartStore } from '../stores/CartStore';
import { IAcardoVoucherTicketContext } from '../interfaces/IAcardoVoucherTicketContext';

type IAcardoMetadata = Pick<ICartItem, 'voucherNumber' | 'externalTransactionId' | 'externalCouponCode'>;

export type GildepassValidationResult =
  | { status: 'valid' }
  | { status: 'invalid'; errorKey: string }
  | { status: 'error'; errorMessage: string };

@Injectable({
  providedIn: 'root'
})
export class PriceCategoryGildepassService {

  private readonly _vouchersService = inject(VouchersService);
  private readonly _cartStore = inject(CartStore);

  private _pendingAcardoMetadata: IAcardoMetadata | null = null;

  public isAcardoCategory(category: IShowPriceCategory): boolean {
    return category.flagAcardo === true;
  }

  public clearPendingMetadata(): void {
    this._pendingAcardoMetadata = null;
  }

  public validateAcardoVoucherForTicket(
    ticket: IAcardoVoucherTicketContext,
    code: string
  ): Observable<GildepassValidationResult> {
    const normalizedCode = this.normalizeCode(code);
    if (this.isCodeAlreadyUsedInBasket(ticket, normalizedCode)) {
      this.clearPendingMetadata();

      return of({
        status: 'invalid',
        errorKey: ApiErrorCodes.PartnerCodeAlreadyUsedForShow
      } as const);
    }

    const showId = ticket.showId > 0 ? ticket.showId : null;
    const valueCents = Math.round(Math.abs(ticket.totalPrice) * 100);

    return this._vouchersService.validateAcardoVoucher(
      null,
      code,
      showId,
      valueCents
    ).pipe(
      map(({ data }) => {
        if (!data.isValid) {
          this.clearPendingMetadata();

          return {
            status: 'invalid',
            errorKey: data.message ?? 'AcardoVoucherInvalid'
          } as const;
        }

        this._pendingAcardoMetadata = {
          voucherNumber: data.voucherCode?.trim() || code.trim(),
          externalTransactionId: data.externalTransactionId ?? null,
          externalCouponCode: data.externalCouponCode ?? null
        };

        return { status: 'valid' } as const;
      }),
      catchError(({ error }: ApiErrorResponse) => {
        this.clearPendingMetadata();

        return of({
          status: 'error',
          errorMessage: error.message
        } as const);
      })
    );
  }

  public enrichTicketsForPriceCategoryChange(
    ticketsToUpdate: ICartItem[],
    selectedPriceCategory: IShowPriceCategory,
    shouldApplyAcardoMetadata: boolean
  ): ICartItem[] {
    if (!this.isAcardoCategory(selectedPriceCategory)) {
      this.clearPendingMetadata();

      return ticketsToUpdate.map(ticket => ({
        ...ticket,
        voucherNumber: null,
        externalCouponCode: null,
        externalTransactionId: null
      }));
    }

    if (!shouldApplyAcardoMetadata || !this._pendingAcardoMetadata) {
      return ticketsToUpdate;
    }

    return ticketsToUpdate.map(ticket => ({
      ...ticket,
      voucherNumber: this._pendingAcardoMetadata.voucherNumber ?? null,
      externalCouponCode: this._pendingAcardoMetadata.externalCouponCode ?? null,
      externalTransactionId: this._pendingAcardoMetadata.externalTransactionId ?? null
    }));
  }

  private isCodeAlreadyUsedInBasket(currentTicket: IAcardoVoucherTicketContext, code: string): boolean {
    if (!code) {
      return false;
    }

    const showId = currentTicket.showId;

    if (showId == null || showId <= 0) {
      return false;
    }

    return this._cartStore.cartItemsTickets().some(ticket => {
      if (ticket.showId !== showId) {
        return false;
      }

      const isCurrentTicket = ticket.itemId === currentTicket.itemId && ticket.position === currentTicket.position;
      
      if (isCurrentTicket) {
        return false;
      }

      return this.normalizeCode(ticket.voucherNumber ?? '') === code;
    });
  }

  private normalizeCode(code: string): string {
    return code?.trim().toLowerCase() ?? '';
  }
}
